import { useEffect, useState } from "react";
import { Check, ChevronDown, Globe2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, apiError } from "@/lib/api";
import { Flag } from "@/components/Flag";
import { toast } from "sonner";

// Which countries the daily news comes from — up to three.
export const CountryPicker = ({ onChange }) => {
  const [all, setAll] = useState([]);
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/news/countries")
      .then(({ data }) => { setAll(data.all); setMine(data.mine); })
      .catch((e) => console.error("could not load the country list", e));
  }, []);

  const pick = async (name) => {
    const next = mine.includes(name)
      ? mine.filter((c) => c !== name)
      : [...mine, name];
    if (next.length > 3) return toast.info("Three countries is plenty for one feed");
    try {
      const { data } = await api.put("/news/countries", { countries: next });
      setMine(data.mine);
      onChange(data.mine);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    }
  };

  const shown = mine.length === 0 ? "Anywhere near me"
    : mine.length === 1 ? mine[0] : `${mine[0]} +${mine.length - 1}`;

  const codeOf = (name) => all.find((c) => c.name === name)?.code || "";

  return (
    <span className="inline-flex items-center gap-2" data-testid="country-picker">
      <span className="text-[11px] uppercase tracking-widest text-ink-soft">News from</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="country-picker-open"
            aria-expanded={open}
            aria-label="Pick the countries your news comes from"
            className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-white/15 bg-panel px-3 text-sm transition-colors hover:border-brand/50"
          >
            {mine.length === 0
              ? <Globe2 className="h-3.5 w-3.5 text-brand" />
              : mine.slice(0, 3).map((name) => (
                  <Flag key={name} country={codeOf(name)} className="h-[14px] w-5"
                        testId={`country-chosen-${codeOf(name)}`} />
                ))}
            <span className="max-w-[10rem] truncate">{shown}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2" data-testid="country-picker-panel">
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-ink-soft">
            Pick up to three — the feed comes only from them
          </p>
          <div className="cml-scroll max-h-64 overflow-y-auto">
            {all.map((c) => (
              <button
                key={c.name}
                data-testid={`country-option-${c.code}`}
                onClick={() => pick(c.name)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm transition-colors hover:bg-surfacealt"
              >
                <Flag country={c.code} testId={`country-flag-${c.code}`} />
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                {mine.includes(c.name) && <Check className="h-4 w-4 shrink-0 text-brand" />}
              </button>
            ))}
          </div>
          {mine.length > 0 && (
            <button
              data-testid="country-clear"
              onClick={async () => {
                const { data } = await api.put("/news/countries", { countries: [] });
                setMine(data.mine);
                onChange(data.mine);
              }}
              className="mt-1 w-full rounded-lg px-3 py-2 text-start text-xs text-ink-soft hover:bg-surfacealt"
            >
              Back to news near me
            </button>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
};
